// @givmo/board — "Where it's recorded (optional)": the act a committee fact may be linked to
// (`board-committees.md` §3.3, §4.5).
//
// The acts are the backend's own list for this reader: a motion recorded as carried (its own words
// beside its meeting, so two motions of one meeting read apart), an executed written consent, or a
// record in the minute book. None chosen is the Owner's own entry. Board never reads a motion's words
// or a consent's text to decide whether it established, dissolved or appointed anything: the choice is
// the Owner's, and the backend refuses only an act that isn't one of those three.

import { useQuery } from '@tanstack/react-query';
import type { BoardCommitteeRecordedIn } from '@givmo/api-client';
import { Select } from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { extractCommitteeSources, sourceChoiceLabel } from './committees-decode';
import { committeesWords as w } from './committees-words';

const NONE = 'none';

/** `kind:id`, the picker's value for one act (both halves are the backend's own). */
export function recordedInValue(recordedIn: BoardCommitteeRecordedIn | null): string {
  return recordedIn === null ? NONE : `${recordedIn.kind}:${recordedIn.id}`;
}

export function RecordedInPicker({
  orgRef,
  value,
  onChange,
  disabled,
  testId,
}: {
  orgRef: string;
  value: BoardCommitteeRecordedIn | null;
  onChange: (next: BoardCommitteeRecordedIn | null) => void;
  disabled?: boolean;
  testId: string;
}) {
  const operations = useBoardOperations();
  const actsQuery = useQuery({
    ...operations.officerListBoardCommitteeSourcesOptions({ path: { org_ref: orgRef } }),
    select: extractCommitteeSources,
  });
  const acts = actsQuery.data ?? [];
  const options = [
    { value: NONE, label: w.recordedInNone },
    ...acts.map((act) => ({ value: `${act.kind}:${act.id}`, label: sourceChoiceLabel(act) })),
  ];
  // A link the reader can no longer list (an act now out of their reach) keeps its own value, so a
  // save never drops it unseen.
  const current = recordedInValue(value);
  if (!options.some((option) => option.value === current)) {
    options.push({ value: current, label: w.recordedInHidden });
  }

  return (
    <Select
      label={w.recordedInLabel}
      appearance="saas"
      value={current}
      disabled={disabled === true || actsQuery.isPending}
      onValueChange={(next) => {
        if (next === null || next === NONE) {
          onChange(null);
          return;
        }
        const [kind, id] = next.split(':');
        if (kind === undefined || id === undefined) return;
        onChange({ kind: kind as BoardCommitteeRecordedIn['kind'], id });
      }}
      options={options}
      testId={testId}
    />
  );
}
